import { ref, computed } from "vue";
import { defineStore } from "pinia";
import type { Variant } from "@cybersentriq/frontend-component-library/dist/types/src/components/pill/types";

export type Pill = { variant: Variant; text: string };
export type Info = { docsLink: string; text: string };

export type PageMeta = {
  title: string;
  subtitle?: string;
  icon?: string;
  pill?: Pill;
  info?: Info;
  mspName: string;
  customerName: string;
  parentPath: string;
};

export type TitleOptions = Pick<PageMeta, "title" | "icon" | "pill" | "info">;

export const usePageMetaStore = defineStore("pageMeta", () => {
  const title = ref<string>("");
  const subtitle = ref<string | undefined>(undefined);
  const icon = ref<string | undefined>(undefined);
  const pill = ref<Pill | null>(null);
  const info = ref<Info | null>(null);
  const mspName = ref<string>("msp");
  const customerName = ref<string>("customer");
  const parentPath = ref<string>("");

  const pageMeta = computed<PageMeta>(() => ({
    title: title.value,
    subtitle: subtitle.value,
    icon: icon.value,
    pill: pill.value ?? undefined,
    info: info.value ?? undefined,
    mspName: mspName.value,
    customerName: customerName.value,
    parentPath: parentPath.value,
  }));

  const setPageTitle = (options: TitleOptions) => {
    const {
      title: newTitle,
      icon: newIcon,
      pill: newPill,
      info: newInfo,
    } = options;

    document.title = newTitle;
    pill.value = newPill ?? null;
    info.value = newInfo ?? null;
    title.value = newTitle;
    icon.value = newIcon ?? undefined;
  };

  // Kept separate from setPageTitle so a persistent page subtitle (e.g. the
  // embedded "read-only" note) is not cleared by routine title updates.
  const setSubtitle = (text: string) => {
    subtitle.value = text || undefined;
  };

  const setParentPath = (path: string) => {
    parentPath.value = path;
  };

  const setMSP = (name: string) => {
    mspName.value = name;
  };

  const setCustomerName = (name: string) => {
    customerName.value = name;
  };

  return {
    title,
    subtitle,
    icon,
    pill,
    info,
    mspName,
    customerName,
    parentPath,
    pageMeta,
    setPageTitle,
    setSubtitle,
    setParentPath,
    setMSP,
    setCustomerName,
  };
});
